import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

// Public pages
import LandingPage from "@/pages/LandingPage";
import PricingPage from "@/pages/PricingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import PublicCatalog from "@/pages/PublicCatalog";
import PublicProduct from "@/pages/PublicProduct";
import PublicCategory from "@/pages/PublicCategory";
import PublicLegal from "@/pages/PublicLegal";
import LandingLegal from "@/pages/LandingLegal";

// Dashboard pages
import DashboardLayout from "@/components/DashboardLayout";
import DashboardHome from "@/pages/dashboard/DashboardHome";
import ShopsList from "@/pages/dashboard/ShopsList";
import ShopSettings from "@/pages/dashboard/ShopSettings";
import ShopCategories from "@/pages/dashboard/ShopCategories";
import ShopProducts from "@/pages/dashboard/ShopProducts";
import ShopQR from "@/pages/dashboard/ShopQR";
import ShopAnalytics from "@/pages/dashboard/ShopAnalytics";
import ShopBanners from "@/pages/dashboard/ShopBanners";
import ShopExport from "@/pages/dashboard/ShopExport";
import ShopPageBuilder from "@/pages/dashboard/ShopPageBuilder";
import ShopStaff from "@/pages/dashboard/ShopStaff";
import ShopLegal from "@/pages/dashboard/ShopLegal";
import BillingPage from "@/pages/dashboard/BillingPage";

// Admin
import AdminPage from "@/pages/AdminPage";

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }
  
  if (!isAuthenticated || user?.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/s/:slug" element={<PublicCatalog />} />
      <Route path="/s/:slug/p/:productId" element={<PublicProduct />} />
      <Route path="/s/:slug/c/:categoryId" element={<PublicCategory />} />
      <Route path="/s/:slug/legal/:pageType" element={<PublicLegal />} />
      <Route path="/legal/:pageType" element={<LandingLegal />} />
      
      {/* Dashboard routes */}
      <Route path="/app" element={
        <ProtectedRoute>
          <DashboardLayout />
        </ProtectedRoute>
      }>
        <Route index element={<DashboardHome />} />
        <Route path="shops" element={<ShopsList />} />
        <Route path="shops/:shopId/settings" element={<ShopSettings />} />
        <Route path="shops/:shopId/categories" element={<ShopCategories />} />
        <Route path="shops/:shopId/products" element={<ShopProducts />} />
        <Route path="shops/:shopId/banners" element={<ShopBanners />} />
        <Route path="shops/:shopId/qr" element={<ShopQR />} />
        <Route path="shops/:shopId/analytics" element={<ShopAnalytics />} />
        <Route path="shops/:shopId/export" element={<ShopExport />} />
        <Route path="shops/:shopId/page-builder" element={<ShopPageBuilder />} />
        <Route path="shops/:shopId/staff" element={<ShopStaff />} />
        <Route path="shops/:shopId/legal" element={<ShopLegal />} />
        <Route path="billing" element={<BillingPage />} />
      </Route>
      
      {/* Admin route */}
      <Route path="/admin" element={
        <AdminRoute>
          <AdminPage />
        </AdminRoute>
      } />
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  useEffect(() => {
    const badge = document.getElementById('emergent-badge');
    if (badge) badge.remove();
    const observer = new MutationObserver(() => {
      const el = document.getElementById('emergent-badge');
      if (el) el.remove();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
          <Toaster richColors position="top-right" />
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
